/** @format */

import {
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Avatar,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { CheckCircle } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { XCircle } from "@phosphor-icons/react/dist/ssr/XCircle";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import InfoIcon from "@mui/icons-material/Info";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WorkIcon from "@mui/icons-material/Work";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	fetchAllJoinRequestsAsAdmin,
	approveJoinRequest,
	rejectJoinRequest,
} from "../../../redux/actions/circleAction";
import {
	selectJoinRequestsApplications,
	selectIsLoadingJoinRequests,
	selectIsApprovingJoinRequest,
	selectIsRejectingJoinRequest,
} from "../../../redux/selectors/circleSelector";
import {
	LoadingSpinner,
	SuccessNotification,
} from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { AdminJoinRequestItem } from "../../../utility/types/circleType";

const JoinRequestApplications = () => {
	const dispatch = useDispatch<AppDispatch>();
	const joinRequestsData = useSelector(selectJoinRequestsApplications);
	const isLoading = useSelector(selectIsLoadingJoinRequests);
	const isApproving = useSelector(selectIsApprovingJoinRequest);
	const isRejecting = useSelector(selectIsRejectingJoinRequest);
	const [openNav, setOpenNav] = useState(false);

	// Track which specific join request is being processed
	const [processingRequestId, setProcessingRequestId] = useState<string | null>(
		null,
	);
	// Track if modal action is processing
	const [isProcessing, setIsProcessing] = useState(false);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [confirmDialog, setConfirmDialog] = useState<{
		open: boolean;
		type: "approve" | "reject" | null;
		joinRequestId: string | null;
		userName: string;
		circleName: string;
	}>({
		open: false,
		type: null,
		joinRequestId: null,
		userName: "",
		circleName: "",
	});
	const [detailsModal, setDetailsModal] = useState<{
		open: boolean;
		joinRequest: AdminJoinRequestItem | null;
	}>({
		open: false,
		joinRequest: null,
	});
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error" | "info" | "warning",
	});

	// Fetch data on mount
	useEffect(() => {
		dispatch(fetchAllJoinRequestsAsAdmin());
	}, [dispatch]);

	const handleRefresh = async () => {
		try {
			await dispatch(fetchAllJoinRequestsAsAdmin()).unwrap();
			setNotification({
				open: true,
				message: "Join requests refreshed successfully",
				severity: "success",
			});
		} catch (err: any) {
			const errorMessage = err?.message || err?.toString() || "";
			// Provide user-friendly error messages
			let friendlyMessage = "Failed to refresh join requests";

			if (errorMessage.toLowerCase().includes("network")) {
				friendlyMessage =
					"Network error. Please check your connection and try again.";
			}

			setNotification({
				open: true,
				message: friendlyMessage,
				severity: "error",
			});
		}
	};

	const handleOpenConfirmDialog = (
		type: "approve" | "reject",
		joinRequestId: string,
		userName: string,
		circleName: string,
	) => {
		setConfirmDialog({
			open: true,
			type,
			joinRequestId,
			userName,
			circleName,
		});
	};

	const handleCloseConfirmDialog = () => {
		// Clear processing states when closing dialog
		setProcessingRequestId(null);
		setIsProcessing(false);
		setConfirmDialog({
			open: false,
			type: null,
			joinRequestId: null,
			userName: "",
			circleName: "",
		});
	};

	const handleOpenDetailsModal = (joinRequest: AdminJoinRequestItem) => {
		setDetailsModal({
			open: true,
			joinRequest,
		});
	};

	const handleCloseDetailsModal = () => {
		setDetailsModal({
			open: false,
			joinRequest: null,
		});
	};

	const handleConfirmAction = async () => {
		const { type, joinRequestId } = confirmDialog;
		if (!type || !joinRequestId) return;

		// Set the processing states
		setProcessingRequestId(joinRequestId);
		setIsProcessing(true);

		try {
			let message = "";
			if (type === "approve") {
				await dispatch(approveJoinRequest(joinRequestId)).unwrap();
				message = "Join request approved successfully";
			} else if (type === "reject") {
				await dispatch(rejectJoinRequest(joinRequestId)).unwrap();
				message = "Join request rejected successfully";
			}

			// Refresh the join requests list after successful action
			await dispatch(fetchAllJoinRequestsAsAdmin()).unwrap();

			setNotification({
				open: true,
				message,
				severity: "success",
			});
			handleCloseConfirmDialog();
		} catch (err: any) {
			// Clear processing states on error
			setProcessingRequestId(null);
			setIsProcessing(false);

			const errorMessage = err?.message || err?.toString() || "";
			// Sanitize technical error messages
			let friendlyMessage = `Failed to ${type} join request`;

			// Detect validation errors
			if (
				errorMessage.includes("validation failed") ||
				errorMessage.includes("Cast to") ||
				errorMessage.includes("embedded failed")
			) {
				friendlyMessage = `Unable to ${type} the request. Please refresh and try again.`;
			}
			// Detect not found errors
			else if (errorMessage.toLowerCase().includes("not found")) {
				friendlyMessage =
					"This request no longer exists. Refreshing the list...";
				setTimeout(() => dispatch(fetchAllJoinRequestsAsAdmin()), 1500);
			}
			// Detect already processed errors
			else if (errorMessage.toLowerCase().includes("already")) {
				friendlyMessage = "This request has already been processed.";
			}
			// Network errors
			else if (errorMessage.toLowerCase().includes("network")) {
				friendlyMessage =
					"Network error. Please check your connection and try again.";
			}

			setNotification({
				open: true,
				message: friendlyMessage,
				severity: "error",
			});
		}
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleCloseNotification = () => {
		setNotification((prev) => ({ ...prev, open: false }));
	};

	// Filter join requests based on search and status
	const filteredJoinRequests = (joinRequestsData?.joinRequests || []).filter(
		(request: AdminJoinRequestItem) => {
			// Status filter
			if (statusFilter !== "all") {
				if (request.status.toLowerCase() !== statusFilter.toLowerCase()) {
					return false;
				}
			}

			// Search filter
			if (searchQuery === "") return true;

			const searchLower = searchQuery.toLowerCase();
			const userNameMatch =
				`${request.userId.firstName} ${request.userId.lastName}`
					.toLowerCase()
					.includes(searchLower);
			const emailMatch = request.userId.email
				.toLowerCase()
				.includes(searchLower);
			const circleNameMatch = request.circleId.circleName
				.toLowerCase()
				.includes(searchLower);

			return userNameMatch || emailMatch || circleNameMatch;
		},
	);

	const paginatedJoinRequests = filteredJoinRequests.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const getStatusColor = (
		status: string,
	): "default" | "success" | "error" | "warning" => {
		switch (status?.toLowerCase()) {
			case "approved":
				return "success";
			case "rejected":
				return "error";
			case "pending":
				return "warning";
			default:
				return "default";
		}
	};

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{isLoading ?
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={isLoading} size={45} />
						</Box>
					:	<>
							<Stack spacing={3}>
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">
											Join Request Applications
										</Typography>
										<Typography color="text.secondary" variant="body2">
											Review and manage user join requests for circles
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<Tooltip title="Refresh">
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												<ArrowsClockwise />
											</IconButton>
										</Tooltip>
									</Box>
								</Stack>

								{/* Stats Cards */}
								<Stack direction="row" spacing={2}>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="primary">
											{joinRequestsData?.totalCount || 0}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Total Requests
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="warning.main">
											{
												filteredJoinRequests?.filter(
													(r) => r.status.toLowerCase() === "pending",
												).length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Pending
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="success.main">
											{
												filteredJoinRequests?.filter(
													(r) => r.status.toLowerCase() === "approved",
												).length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Approved
										</Typography>
									</Card>
									<Card sx={{ flex: 1, p: 2 }}>
										<Typography variant="h6" color="error.main">
											{
												filteredJoinRequests?.filter(
													(r) => r.status.toLowerCase() === "rejected",
												).length
											}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Rejected
										</Typography>
									</Card>
								</Stack>

								{/* Filters and Search */}
								<Card>
									<Stack
										direction="row"
										spacing={2}
										sx={{ p: 2 }}
										alignItems="center">
										<OutlinedInput
											value={searchQuery}
											onChange={(e) => setSearchQuery(e.target.value)}
											fullWidth
											placeholder="Search by user name, email, or circle name..."
											startAdornment={
												<InputAdornment position="start">
													<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
												</InputAdornment>
											}
										/>
										<Stack direction="row" spacing={1}>
											<Button
												variant={
													statusFilter === "all" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("all")}
												size="small">
												All
											</Button>
											<Button
												variant={
													statusFilter === "pending" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("pending")}
												size="small"
												color="warning">
												Pending
											</Button>
											<Button
												variant={
													statusFilter === "approved" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("approved")}
												size="small"
												color="success">
												Approved
											</Button>
											<Button
												variant={
													statusFilter === "rejected" ? "contained" : "outlined"
												}
												onClick={() => setStatusFilter("rejected")}
												size="small"
												color="error">
												Rejected
											</Button>
										</Stack>
									</Stack>
								</Card>
								<Card>
									{isLoading ?
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												minHeight: 400,
											}}>
											<CircularProgress />
										</Box>
									:	<>
											<Box sx={{ overflowX: "auto" }}>
												<Table sx={{ minWidth: "800px" }}>
													<TableHead>
														<TableRow>
															<TableCell>User</TableCell>
															<TableCell>Circle</TableCell>
															<TableCell>Rating</TableCell>
															<TableCell>Status</TableCell>
															<TableCell>Request Date</TableCell>
															<TableCell align="center">More Info</TableCell>
															<TableCell align="center">Actions</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{paginatedJoinRequests.length === 0 ?
															<TableRow>
																<TableCell colSpan={7} align="center">
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		No join requests found
																	</Typography>
																</TableCell>
															</TableRow>
														:	paginatedJoinRequests.map(
																(request: AdminJoinRequestItem) => (
																	<TableRow hover key={request._id}>
																		<TableCell>
																			<Stack
																				direction="row"
																				spacing={2}
																				alignItems="center">
																				<Avatar
																					src={request.userId.profileImage}
																					sx={{ width: 40, height: 40 }}>
																					{`${request.userId.firstName[0]}${request.userId.lastName[0]}`}
																				</Avatar>
																				<Box>
																					<Typography variant="subtitle2">
																						{`${request.userId.firstName} ${request.userId.lastName}`}
																					</Typography>
																					<Typography
																						variant="caption"
																						color="text.secondary">
																						{request.userId.email}
																					</Typography>
																				</Box>
																			</Stack>
																		</TableCell>
																		<TableCell>
																			<Stack spacing={0.5}>
																				<Typography variant="subtitle2">
																					{request.circleId.circleName}
																				</Typography>
																				<Typography
																					variant="caption"
																					color="text.secondary">
																					Leader:{" "}
																					{`${request.circleId.leader.firstName} ${request.circleId.leader.lastName}`}
																				</Typography>
																			</Stack>
																		</TableCell>
																		<TableCell>
																			<Chip
																				label={`⭐ ${request.rating.toFixed(
																					1,
																				)}`}
																				size="small"
																				variant="outlined"
																			/>
																		</TableCell>
																		<TableCell>
																			<Chip
																				label={request.status}
																				color={getStatusColor(request.status)}
																				size="small"
																			/>
																		</TableCell>
																		<TableCell>
																			{dayjs(request.created_At).format(
																				"MMM D, YYYY",
																			)}
																		</TableCell>
																		<TableCell align="center">
																			<Tooltip title="More Info">
																				<IconButton
																					color="primary"
																					size="small"
																					onClick={() =>
																						handleOpenDetailsModal(request)
																					}>
																					<InfoIcon />
																				</IconButton>
																			</Tooltip>
																		</TableCell>
																		<TableCell>
																			<Stack
																				direction="row"
																				spacing={1}
																				justifyContent="center">
																				{request.status === "Pending" && (
																					<>
																						<Tooltip title="Approve">
																							<IconButton
																								color="success"
																								size="small"
																								disabled={
																									(isApproving ||
																										isRejecting) &&
																									processingRequestId ===
																										request._id
																								}
																								onClick={() =>
																									handleOpenConfirmDialog(
																										"approve",
																										request._id,
																										`${request.userId.firstName} ${request.userId.lastName}`,
																										request.circleId.circleName,
																									)
																								}>
																								{(
																									isApproving &&
																									processingRequestId ===
																										request._id
																								) ?
																									<CircularProgress size={20} />
																								:	<CheckCircle />}
																							</IconButton>
																						</Tooltip>
																						<Tooltip title="Reject">
																							<IconButton
																								color="error"
																								size="small"
																								disabled={
																									(isApproving ||
																										isRejecting) &&
																									processingRequestId ===
																										request._id
																								}
																								onClick={() =>
																									handleOpenConfirmDialog(
																										"reject",
																										request._id,
																										`${request.userId.firstName} ${request.userId.lastName}`,
																										request.circleId.circleName,
																									)
																								}>
																								{(
																									isRejecting &&
																									processingRequestId ===
																										request._id
																								) ?
																									<CircularProgress size={20} />
																								:	<XCircle />}
																							</IconButton>
																						</Tooltip>
																					</>
																				)}
																				{request.status !== "Pending" && (
																					<Typography
																						variant="caption"
																						color="text.secondary">
																						No actions available
																					</Typography>
																				)}
																			</Stack>
																		</TableCell>
																	</TableRow>
																),
															)
														}
													</TableBody>
												</Table>
											</Box>
											<Divider />
											<TablePagination
												component="div"
												count={filteredJoinRequests.length}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
												page={page}
												rowsPerPage={rowsPerPage}
												rowsPerPageOptions={[5, 10, 25]}
											/>
										</>
									}
								</Card>
								{/* Confirmation Dialog */}
								<Dialog
									open={confirmDialog.open}
									onClose={!isProcessing ? handleCloseConfirmDialog : undefined}
									aria-labelledby="confirm-dialog-title">
									<DialogTitle id="confirm-dialog-title">
										{confirmDialog.type === "approve" && "Approve Join Request"}
										{confirmDialog.type === "reject" && "Reject Join Request"}
									</DialogTitle>
									<DialogContent>
										{isProcessing ?
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													gap: 2,
													py: 3,
												}}>
												<CircularProgress size={40} />
												<Typography variant="body2" color="text.secondary">
													Processing...
												</Typography>
											</Box>
										:	<DialogContentText>
												{confirmDialog.type === "approve" &&
													`Are you sure you want to approve ${confirmDialog.userName}'s request to join ${confirmDialog.circleName}? They will be added as a member.`}
												{confirmDialog.type === "reject" &&
													`Are you sure you want to reject ${confirmDialog.userName}'s request to join ${confirmDialog.circleName}? They will need to wait 30 days to reapply.`}
											</DialogContentText>
										}
									</DialogContent>
									<DialogActions>
										<Button
											onClick={handleCloseConfirmDialog}
											color="inherit"
											disabled={isProcessing}>
											Cancel
										</Button>
										<Button
											onClick={handleConfirmAction}
											color={
												confirmDialog.type === "approve" ? "success" : "error"
											}
											variant="contained"
											disabled={isProcessing}
											autoFocus>
											{confirmDialog.type === "approve" && "Approve"}
											{confirmDialog.type === "reject" && "Reject"}
										</Button>
									</DialogActions>
								</Dialog>
								{/* Join Request Details Modal */}
								<Dialog
									open={detailsModal.open}
									onClose={handleCloseDetailsModal}
									maxWidth="md"
									fullWidth
									PaperProps={{
										sx: {
											borderRadius: 3,
											overflow: "hidden",
										},
									}}>
									<DialogTitle
										sx={{
											background:
												"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
											color: "white",
											py: 2.5,
										}}>
										<Typography variant="h5" fontWeight={600}>
											Join Request Details
										</Typography>
									</DialogTitle>
									<DialogContent sx={{ p: 0 }}>
										{detailsModal.joinRequest && (
											<Box>
												{/* User Profile Header */}
												<Box
													sx={{
														background:
															"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
														pt: 0,
														pb: 8,
														px: 3,
													}}>
													<Stack
														direction="row"
														spacing={3}
														alignItems="center">
														<Avatar
															src={detailsModal.joinRequest.userId.profileImage}
															sx={{
																width: 80,
																height: 80,
																border: "4px solid white",
																boxShadow: 3,
															}}>
															{`${detailsModal.joinRequest.userId.firstName?.[0]}${detailsModal.joinRequest.userId.lastName?.[0]}`}
														</Avatar>
														<Box>
															<Typography
																variant="h5"
																fontWeight={600}
																color="white">
																{detailsModal.joinRequest.userId.firstName}{" "}
																{detailsModal.joinRequest.userId.lastName}
															</Typography>
															<Typography
																variant="body2"
																sx={{ color: "rgba(255,255,255,0.85)" }}>
																{detailsModal.joinRequest.userId.email}
															</Typography>
														</Box>
													</Stack>
												</Box>

												{/* Status Cards - Overlapping Design */}
												<Box sx={{ px: 3, mt: -5 }}>
													<Grid container spacing={2}>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		(
																			detailsModal.joinRequest.status?.toLowerCase() ===
																			"approved"
																		) ?
																			"linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
																		: (
																			detailsModal.joinRequest.status?.toLowerCase() ===
																			"rejected"
																		) ?
																			"linear-gradient(135deg, #eb3349 0%, #f45c43 100%)"
																		:	"linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
																	color: "white",
																}}>
																<HourglassEmptyIcon
																	sx={{ fontSize: 32, mb: 1 }}
																/>
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Request Status
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.joinRequest.status}
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
																	color: "white",
																}}>
																<StarIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	User Rating
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.joinRequest.rating.toFixed(1)} /
																	5.0
																</Typography>
															</Card>
														</Grid>
														<Grid item xs={12} sm={4}>
															<Card
																sx={{
																	p: 2.5,
																	borderRadius: 2,
																	boxShadow: 3,
																	textAlign: "center",
																	background:
																		"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
																	color: "white",
																}}>
																<GroupsIcon sx={{ fontSize: 32, mb: 1 }} />
																<Typography
																	variant="body2"
																	sx={{ opacity: 0.9 }}>
																	Circle Members
																</Typography>
																<Typography variant="h6" fontWeight={700}>
																	{detailsModal.joinRequest.circleId.members
																		?.length || 0}
																</Typography>
															</Card>
														</Grid>
													</Grid>
												</Box>

												{/* Circle Information Section */}
												<Box sx={{ px: 3, py: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Circle Information
													</Typography>
													<Card
														variant="outlined"
														sx={{
															borderRadius: 2,
															transition: "all 0.2s ease-in-out",
															"&:hover": {
																boxShadow: 2,
																borderColor: "primary.main",
															},
														}}>
														<Stack
															direction="row"
															spacing={2}
															alignItems="center"
															sx={{ p: 2 }}>
															<Avatar
																src={
																	detailsModal.joinRequest.circleId.circleImage
																}
																alt={
																	detailsModal.joinRequest.circleId.circleName
																}
																sx={{
																	width: 56,
																	height: 56,
																	borderRadius: 2,
																}}
																variant="rounded"
															/>
															<Box sx={{ flex: 1 }}>
																<Typography
																	variant="subtitle1"
																	fontWeight={600}>
																	{detailsModal.joinRequest.circleId.circleName}
																</Typography>
																<Stack
																	direction="row"
																	spacing={2}
																	sx={{ mt: 0.5 }}>
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		Leader:{" "}
																		<strong>
																			{
																				detailsModal.joinRequest.circleId.leader
																					.firstName
																			}{" "}
																			{
																				detailsModal.joinRequest.circleId.leader
																					.lastName
																			}
																		</strong>
																	</Typography>
																</Stack>
															</Box>
														</Stack>
													</Card>
												</Box>

												{/* Reason for Joining Section */}
												<Box sx={{ px: 3, pb: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Reason for Joining
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Box sx={{ px: 2.5, py: 2 }}>
															<Typography variant="body1">
																{detailsModal.joinRequest.reason ||
																	"No reason provided"}
															</Typography>
														</Box>
													</Card>
												</Box>

												{/* Employment Information Section */}
												{detailsModal.joinRequest.userId.employer && (
													<Box sx={{ px: 3, pb: 3 }}>
														<Typography
															variant="subtitle1"
															fontWeight={600}
															sx={{ mb: 2, color: "text.secondary" }}>
															Employment Information
														</Typography>
														<Card
															variant="outlined"
															sx={{ borderRadius: 2, overflow: "hidden" }}>
															<Stack divider={<Divider />}>
																{detailsModal.joinRequest.userId.employer
																	.employerName && (
																	<Stack
																		direction="row"
																		justifyContent="space-between"
																		alignItems="center"
																		sx={{ px: 2.5, py: 1.5 }}>
																		<Stack
																			direction="row"
																			spacing={1}
																			alignItems="center">
																			<WorkIcon
																				fontSize="small"
																				color="action"
																			/>
																			<Typography
																				variant="body2"
																				color="text.secondary">
																				Employer
																			</Typography>
																		</Stack>
																		<Typography
																			variant="body1"
																			fontWeight={500}>
																			{
																				detailsModal.joinRequest.userId.employer
																					.employerName
																			}
																		</Typography>
																	</Stack>
																)}
																{detailsModal.joinRequest.userId.employer
																	.income && (
																	<Stack
																		direction="row"
																		justifyContent="space-between"
																		alignItems="center"
																		sx={{ px: 2.5, py: 1.5 }}>
																		<Typography
																			variant="body2"
																			color="text.secondary">
																			Annual Income
																		</Typography>
																		<Chip
																			label={`$${detailsModal.joinRequest.userId.employer.income.toLocaleString()}`}
																			color="success"
																			size="small"
																		/>
																	</Stack>
																)}
																{detailsModal.joinRequest.userId.employer
																	.yearsWithEmployer && (
																	<Stack
																		direction="row"
																		justifyContent="space-between"
																		alignItems="center"
																		sx={{ px: 2.5, py: 1.5 }}>
																		<Typography
																			variant="body2"
																			color="text.secondary">
																			Years with Employer
																		</Typography>
																		<Typography
																			variant="body1"
																			fontWeight={500}>
																			{
																				detailsModal.joinRequest.userId.employer
																					.yearsWithEmployer
																			}{" "}
																			years
																		</Typography>
																	</Stack>
																)}
															</Stack>
														</Card>
													</Box>
												)}

												{/* Request Dates Section */}
												<Box sx={{ px: 3, pb: 3 }}>
													<Typography
														variant="subtitle1"
														fontWeight={600}
														sx={{ mb: 2, color: "text.secondary" }}>
														Request Timeline
													</Typography>
													<Card
														variant="outlined"
														sx={{ borderRadius: 2, overflow: "hidden" }}>
														<Stack divider={<Divider />}>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Stack
																	direction="row"
																	spacing={1}
																	alignItems="center">
																	<CalendarTodayIcon
																		fontSize="small"
																		color="action"
																	/>
																	<Typography
																		variant="body2"
																		color="text.secondary">
																		Requested
																	</Typography>
																</Stack>
																<Typography variant="body1" fontWeight={500}>
																	{dayjs(
																		detailsModal.joinRequest.created_At,
																	).format("MMM D, YYYY [at] h:mm A")}
																</Typography>
															</Stack>
															<Stack
																direction="row"
																justifyContent="space-between"
																alignItems="center"
																sx={{ px: 2.5, py: 1.5 }}>
																<Typography
																	variant="body2"
																	color="text.secondary">
																	Last Updated
																</Typography>
																<Typography variant="body1" fontWeight={500}>
																	{dayjs(
																		detailsModal.joinRequest.updated_At,
																	).format("MMM D, YYYY [at] h:mm A")}
																</Typography>
															</Stack>
															{detailsModal.joinRequest.deniedAt && (
																<Stack
																	direction="row"
																	justifyContent="space-between"
																	alignItems="center"
																	sx={{
																		px: 2.5,
																		py: 1.5,
																		bgcolor: "error.lighter",
																	}}>
																	<Typography
																		variant="body2"
																		color="error.main">
																		Denied At
																	</Typography>
																	<Typography
																		variant="body1"
																		fontWeight={500}
																		color="error.main">
																		{dayjs(
																			detailsModal.joinRequest.deniedAt,
																		).format("MMM D, YYYY [at] h:mm A")}
																	</Typography>
																</Stack>
															)}
														</Stack>
													</Card>
												</Box>
											</Box>
										)}
									</DialogContent>
									<DialogActions sx={{ px: 3, py: 2 }}>
										<Button
											onClick={handleCloseDetailsModal}
											variant="outlined"
											sx={{ borderRadius: 2, px: 3 }}>
											Close
										</Button>
									</DialogActions>
								</Dialog>
								{/* Notification */}
								<SuccessNotification
									open={notification.open}
									onClose={handleCloseNotification}
									severity={notification.severity}
									message={notification.message}
								/>
							</Stack>
						</>
					}
				</Main>
			</Box>
		</React.Fragment>
	);
};

export default JoinRequestApplications;
